import { useRef, useState } from 'react';

interface PanBounds {
  minXOffset: number;
  maxXOffset: number;
  panStepPx: number;
}

// Breathing room between the viewport edge and the leftmost column. The
// cluster's `translateX` is computed off this same constant via the
// `translateX` returned from this hook.
const LEFT_PADDING_PX = 24;

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

export interface PanControllerArgs {
  /**
   * Resets the pan offset whenever this changes. The cluster instance is
   * reused across focus changes (its `key` is anchored to the first node,
   * which doesn't move), so without this the old offset would leave a small
   * focused subgraph rendered off-screen.
   */
  focusedId: string | null;
  /** Measured cluster width (from the parent's resize observer). */
  width: number;
  /** Width of the laid-out graph, max-x minus min-x in node coordinates. */
  graphWidth: number;
  /** Trailing space reserved past the right-most label. */
  rightPadding: number;
  /** Leftmost node's x coordinate (typically negative; the layout centers on 0). */
  minNodeX: number;
  /** Pixels per pan-button click. Usually one column-spacing for visual stability. */
  panStepPx: number;
}

export interface PanController {
  /** Pre-computed translate-x for the cluster's `<g>`. */
  translateX: number;
  canGoLeft: boolean;
  canGoRight: boolean;
  handleLeft: () => void;
  handleRight: () => void;
}

/**
 * Manages horizontal pan state for the cluster's SVG. Bundles three
 * concerns the cluster otherwise interleaves with rendering:
 *
 * - The pan offset (a `useState`) and its reset on `focusedId` change.
 * - A centering offset captured once per layout (via a ref keyed on
 *   `${graphWidth}|${rightPadding}`) and frozen against resizes — re-running
 *   it every render would jolt the view as the user resizes.
 * - The clamp/derived flags / handlers for the pan chevrons.
 *
 * Returns ready-to-use `translateX`, button-enable flags, and click handlers.
 */
export const usePanController = ({
  focusedId,
  width,
  graphWidth,
  rightPadding,
  minNodeX,
  panStepPx,
}: PanControllerArgs): PanController => {
  // Negative values scroll content left, revealing the right side.
  const [xOffset, setXOffset] = useState(0);

  // Reset pan on focus change. Done via a previous-value comparison rather
  // than an effect — see "Adjusting state when a prop changes" in the React
  // docs.
  const [prevFocusedId, setPrevFocusedId] = useState(focusedId);
  if (focusedId !== prevFocusedId) {
    setPrevFocusedId(focusedId);
    setXOffset(0);
  }

  // Captured once per layout on first non-zero width and frozen against
  // resizes; recaptured when `layoutKey` changes. The `width > 0` guard
  // matters because the cluster calls this hook unconditionally (hooks
  // rules) but only renders on non-zero width — without the guard we'd
  // freeze in a width=0 offset and never recompute.
  const centeringRef = useRef<{ offset: number; layoutKey: string }>({ offset: 0, layoutKey: '' });

  // Written every render so pan handlers always read current bounds even if
  // a resize fires between the render that created the handler and its click.
  const boundsRef = useRef<PanBounds>({ minXOffset: 0, maxXOffset: 0, panStepPx });
  const layoutKey = `${graphWidth}|${rightPadding}`;
  if (width > 0 && centeringRef.current.layoutKey !== layoutKey) {
    const extra = width - LEFT_PADDING_PX - rightPadding - graphWidth;
    centeringRef.current = { offset: extra > 0 ? extra / 2 : 0, layoutKey };
  }
  // Fall back to no centering until the new layout is measured at non-zero
  // width, so we don't apply a stale offset captured against the previous
  // layout (or against width=0).
  const centeringOffset = centeringRef.current.layoutKey === layoutKey ? centeringRef.current.offset : 0;

  // Min offset parks the right-most node at `width - rightPadding`.
  // Pins to 0 when the graph fits, disabling both pan buttons.
  const minXOffset = Math.min(0, width - LEFT_PADDING_PX - rightPadding - graphWidth - centeringOffset);
  const maxXOffset = 0;
  const clampedXOffset = clamp(xOffset, minXOffset, maxXOffset);

  boundsRef.current = { minXOffset, maxXOffset, panStepPx };

  return {
    translateX: LEFT_PADDING_PX + centeringOffset - minNodeX + clampedXOffset,
    canGoLeft: clampedXOffset < maxXOffset,
    canGoRight: clampedXOffset > minXOffset,
    handleLeft: () => {
      const { minXOffset: min, maxXOffset: max, panStepPx: step } = boundsRef.current;
      setXOffset((prev) => clamp(prev + step, min, max));
    },
    handleRight: () => {
      const { minXOffset: min, maxXOffset: max, panStepPx: step } = boundsRef.current;
      setXOffset((prev) => clamp(prev - step, min, max));
    },
  };
};
